import { Injectable, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { EMPTY, Observable, Subject, concatMap, filter, interval, share, startWith, tap } from 'rxjs';
import { catchError, debounceTime, map, retry, switchMap } from 'rxjs/operators';

import { ChatNotificationPayload, ChatNotificationType } from '@lib/chat/core/models';
import { ChatMessage, ChatTabFilter, ChatThread, ChatThreadSearchRequest } from '@lib/chat/models/chat.interface';

import { MemberProfile } from '@lib/core/models/member.interface';
import { SystemUserProfile } from '@lib/core/models/system-user.interface';
import { SystemUsersService } from '@lib/core/services/system-users.service';

import { MESSAGE_LIMIT, POLLING_INTERVAL_MESSAGES, POLLING_INTERVAL_THREADS, THREAD_LIMIT } from '../models/chat.const';
import { CHAT_LIB_CONFIG } from '../tokens/chat.tokens';
import { ChatCommunicationService } from './chat-communication.service';
import { ChatNotificationService } from './chat-notification.service';

const EMPTY_GUID: string = '00000000-0000-0000-0000-000000000000';

@Injectable({ providedIn: 'root' })
export class ChatStateService {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly chatService = inject(ChatCommunicationService);
  private readonly chatConfig = inject(CHAT_LIB_CONFIG);
  private readonly notificationService = inject(ChatNotificationService);
  private readonly systemUsersService = inject(SystemUsersService, { optional: true });

  readonly threads = signal<ChatThread[]>([]); // Keep for backwards compatibility
  readonly allThreads = signal<ChatThread[]>([]);
  readonly myThreads = signal<ChatThread[]>([]);
  readonly inQueueThreads = signal<ChatThread[]>([]);
  readonly messages = signal<ChatMessage[]>([]);
  readonly unreads = signal<Map<string, number>>(new Map());
  readonly selectedThread = signal<ChatThread | null>(null);
  readonly profile = signal<MemberProfile | null>(null);
  readonly relatedItems = signal<unknown[]>([]);
  readonly messagesLoading = signal<boolean>(false);
  readonly threadDataLoading = signal<boolean>(false);
  readonly threadsLoading = signal<boolean>(true);
  readonly searchTerm = signal<string>('');
  readonly isSearching = signal<boolean>(false);
  readonly initialThreadsLoading = signal<boolean>(true);
  readonly currentSearchRequest = signal<ChatThreadSearchRequest>({});
  readonly myChatsCount = signal<number>(0);
  readonly inQueueCount = signal<number>(0);
  readonly currentActiveTab = signal<ChatTabFilter>(ChatTabFilter.ALL_CHATS);
  readonly systemUsers = signal<SystemUserProfile[]>([]);
  private readonly ownerUserId = signal<string | null>(null);

  readonly ownUserId = computed(() => this.chatConfig.getUserInfo?.()?.userId || this.chatConfig.ownUserInfo?.userId || null);
  readonly features = computed(() => this.chatConfig.features);

  private initialized = false;
  private lastCountUpdate: number = 0;
  private readonly THROTTLE_DELAY = 2000; // 2 seconds minimum between count updates
  private readonly scroll$ = new Subject<{ before: Date | null; after: Date | null }>();

  init() {
    // init() runs on every ChatComponent mount; wire the singleton's pollers/handlers only once.
    if (this.initialized) return;
    this.initialized = true;

    this.initThreadsPolling();
    this.initSelectedThreadMessagesPolling();
    this.initNotificationHandlers();
    this.initScrollMessagesEffect();
    if (this.features().showThreadTabs && this.systemUsersService) this.loadSystemUsers();
  }

  async loadThreadData(threadId: string) {
    if (!this.chatConfig.resolveThreadData) return;

    try {
      this.threadDataLoading.set(true);
      const threadData = await this.chatConfig.resolveThreadData(threadId);

      if (threadData.thread) {
        this.setSelectedThread(threadData.thread);
        this.setThreadData(threadData.profile || null, threadData.relatedItems || []);
      }
    } catch (error) {
      console.error('Error loading thread data:', error);
    } finally {
      this.threadDataLoading.set(false);
    }
  }

  setSelectedThread(thread: ChatThread) {
    this.selectedThread.set(thread);
    this.loadMessages(thread);
  }

  setThreadData(profile: MemberProfile | null, relatedItems: unknown[] = []) {
    this.profile.set(profile);
    this.relatedItems.set(relatedItems);
  }

  private setInitialThreadData() {
    this.profile.set(null);
    this.messages.set([]);
    this.relatedItems.set([]);
  }

  clearSelectedThread() {
    this.selectedThread.set(null);
    this.threadDataLoading.set(false);
    this.setInitialThreadData();
  }

  upsertThreads(threads: ChatThread[]) {
    if (this.isSearching()) return;
    if (!this.validateUserConsistency()) return;

    const filteredThreads = threads.filter(thread => {
      const isParticipant = thread.userIds?.includes(this.ownUserId());
      const isAssignee = thread.assignees?.includes(this.ownUserId());

      return !(!isParticipant && !isAssignee);
    });
    const map = new Map<string, ChatThread>();

    this.threads().forEach(thread => map.set(thread.id, thread));
    filteredThreads.forEach(thread => map.set(thread.id, thread));

    const hasNewThreads: boolean = filteredThreads.some(thread => !this.threads().find(existing => existing.id === thread.id));
    let resultThreads: ChatThread[];

    if (hasNewThreads || this.threads().length === 0) resultThreads = this.sortThreads([...map.values()], this.unreads());
    else resultThreads = this.threads().map(existingThread => (map.get(existingThread.id) ? map.get(existingThread.id) : existingThread));

    if (this.selectedThread()) {
      let updatedSelectedThread = filteredThreads.find(t => t.id === this.selectedThread().id);

      if (!updatedSelectedThread) updatedSelectedThread = threads.find(t => t.id === this.selectedThread().id);
      this.selectedThread.set(updatedSelectedThread);
    }

    const hasSignificantChanges = resultThreads.length !== this.allThreads().length || resultThreads.some(thread => !this.allThreads().find(existing => existing.id === thread.id));
    const hasAssignmentChanges = resultThreads.some(thread => {
      const existing = this.allThreads().find(existing => existing.id === thread.id);

      return existing && JSON.stringify(existing.assignees) !== JSON.stringify(thread.assignees);
    });

    if (hasSignificantChanges || hasAssignmentChanges || this.allThreads().length === 0) this.updateTabSpecificThreads(resultThreads);
    else this.updateCountsFromCurrentState();
  }

  private validateUserConsistency(): boolean {
    const currentUserId = this.ownUserId();
    const lastUserId = this.ownerUserId();

    if (!currentUserId) {
      console.error('User ID is null/undefined - potential authentication issue');
      return false;
    }

    if (lastUserId && lastUserId !== currentUserId) {
      this.clearAllState();
      this.ownerUserId.set(currentUserId);

      return false;
    }

    if (!lastUserId) this.ownerUserId.set(currentUserId);

    return true;
  }

  private clearAllState() {
    this.threads.set([]);
    this.allThreads.set([]);
    this.myThreads.set([]);
    this.inQueueThreads.set([]);
    this.messages.set([]);
    this.selectedThread.set(null);
    this.unreads.set(new Map());
    this.myChatsCount.set(0);
    this.inQueueCount.set(0);
  }

  private updateTabSpecificThreads(allThreads: ChatThread[]) {
    const sortedAllThreads = this.sortThreads(allThreads, this.unreads());

    this.allThreads.set(sortedAllThreads);
    this.updateAllTabSignals(sortedAllThreads);
  }

  upsertMessages(messages: ChatMessage[]) {
    if (!this.validateUserConsistency()) return;

    const filteredMessages = messages.filter(message => {
      if (this.selectedThread() && message.threadId !== this.selectedThread().id) return false;
      if (this.selectedThread()) {
        return (
          message.userId !== this.ownerUserId() ||
          !this.selectedThread().userIds?.includes(this.ownerUserId()) ||
          message.userId !== this.selectedThread().ownerId ||
          message.userId !== EMPTY_GUID
        );
      }

      return true;
    });
    const map = new Map<string, ChatMessage>();

    this.messages().forEach(m => map.set(m.id, m));
    filteredMessages.forEach(m => map.set(m.id, m));

    const sorted = [...map.values()].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

    this.messages.set(sorted);
    this.syncThreadWithLatestMessage(filteredMessages);
  }

  private syncThreadWithLatestMessage(messages: ChatMessage[]) {
    if (messages.length === 0) return;

    const latestMessagesByThread = new Map<string, ChatMessage>();

    messages.forEach(message => {
      const existing = latestMessagesByThread.get(message.threadId);

      if (!existing || new Date(message.createdAt) > new Date(existing.createdAt)) latestMessagesByThread.set(message.threadId, message);
    });

    latestMessagesByThread.forEach((latestMessage, threadId) => this.updateThreadLastMessage(threadId, latestMessage));
  }

  private updateThreadLastMessage(threadId: string, latestMessage: ChatMessage) {
    const updateThreadData = (thread: ChatThread): ChatThread => {
      if (thread.id !== threadId) return thread;
      return {
        ...thread,
        latestSentMessage: latestMessage.content,
        lastSentAt: latestMessage.createdAt,
      };
    };
    const updatedAllThreads = this.allThreads().map(updateThreadData);
    const updatedMyThreads = this.myThreads().map(updateThreadData);
    const updatedInQueueThreads = this.inQueueThreads().map(updateThreadData);
    const updatedThreads = this.threads().map(updateThreadData);

    if (updatedAllThreads.some((t, i) => t !== this.allThreads()[i])) this.allThreads.set(updatedAllThreads);
    if (updatedMyThreads.some((t, i) => t !== this.myThreads()[i])) this.myThreads.set(updatedMyThreads);
    if (updatedInQueueThreads.some((t, i) => t !== this.inQueueThreads()[i])) this.inQueueThreads.set(updatedInQueueThreads);
    if (updatedThreads.some((t, i) => t !== this.threads()[i])) this.threads.set(updatedThreads);
    if (this.selectedThread() && this.selectedThread().id === threadId) this.selectedThread.set(updateThreadData(this.selectedThread()));
  }

  markThreadAsRead(threadId: string) {
    const map = this.unreads();

    map.set(threadId, 0);
    this.unreads.set(new Map(map));
    this.chatService.markThreadRead(threadId).subscribe();
  }

  onSendMessage($event: string | FormData) {
    if (!this.selectedThread()) return;

    let message: string;
    let fileName: string;
    let fileLink: string;

    if (typeof $event === 'string') message = $event;
    else {
      fileName = $event.get('name') as string;
      fileLink = $event.get('link') as string;
    }

    const tempId = `temp_${crypto.randomUUID()}`;
    const externalId = crypto.randomUUID();
    const messagePayload: Partial<ChatMessage> = {
      content: message ?? '',
      threadId: this.selectedThread().id,
      topicId: this.selectedThread().topicIds?.[0],
      messageType: 'text',
      externalId,
      media: { [fileName]: fileLink },
      senderName: this.getCurrentSenderName(),
    };

    if (typeof $event === 'string') delete messagePayload.media;

    const pendingMessage: ChatMessage = {
      id: tempId,
      topicId: messagePayload.topicId || '',
      threadId: messagePayload.threadId || '',
      userId: this.ownUserId() || '00000000-0000-0000-0000-000000000000',
      externalId,
      senderName: this.getCurrentSenderName(),
      content: messagePayload.content || '',
      messageType: messagePayload.messageType || 'text',
      metadata: messagePayload.metadata || {},
      media: messagePayload.media || {},
      createdAt: new Date(),
      updatedAt: new Date(),
      deletedAt: new Date(0),
      readReceipts: {},
      isPending: true,
      hasFailed: false,
    };

    this.upsertMessages([pendingMessage]);

    this.chatService
      .sendMessage(messagePayload)
      .pipe(
        tap(result => this.replacePendingMessage(tempId, result)),
        switchMap(result => this.chatService.getThread(result.threadId))
      )
      .subscribe({
        next: thread => {
          const wasAssignedToMe = this.selectedThread()?.assignees?.includes(this.ownUserId());
          const isNowAssignedToMe = thread.assignees?.includes(this.ownUserId());

          if (this.features().showThreadTabs) {
            this.updateThreadInAllLists(thread);

            if (!wasAssignedToMe && isNowAssignedToMe && this.currentActiveTab() !== ChatTabFilter.MY_CHATS) this.navigateToTab(ChatTabFilter.MY_CHATS);
          } else this.updateThreadInSimpleList(thread);

          if (this.selectedThread() && this.selectedThread().id === thread.id) this.selectedThread.set(thread);
          this.markThreadAsRead(thread.id);
        },
        error: () => this.markMessageAsFailed(tempId),
      });
  }

  private initScrollMessagesEffect() {
    this.scroll$
      .pipe(
        filter(() => !!this.selectedThread()),
        debounceTime(100),
        switchMap(({ before, after }) => {
          const thread = this.selectedThread();
          return this.chatService.searchChatMessages(thread.id, {
            to: before ?? undefined,
            from: after ?? undefined,
            pageSize: MESSAGE_LIMIT,
          });
        })
      )
      .subscribe(res => {
        this.upsertMessages(res.value);
        this.messagesLoading.set(false);
      });
  }

  private initThreadsPolling() {
    this.threadsLoading.set(true);

    interval(POLLING_INTERVAL_THREADS)
      .pipe(
        share(),
        startWith(0),
        switchMap(() => {
          if (!this.ownUserId()) return EMPTY;

          const searchValue = this.isSearching() ? undefined : this.searchTerm();

          if (!this.features().showThreadTabs) {
            return this.chatService
              .searchThreads({
                value: searchValue,
                pageSize: 100,
                sortBy: 'last_sent_at',
                sortDirection: 'desc',
              })
              .pipe(
                switchMap(threadsResult =>
                  this.chatService.getUnreadCountsByThreadIds(threadsResult.value).pipe(
                    map(unreadsResult => {
                      const unreadsMap = new Map<string, number>();
                      Object.entries(unreadsResult).forEach(([threadId, count]) => unreadsMap.set(threadId, +count));

                      return { threads: threadsResult.value, unreadsMap };
                    })
                  )
                )
              );
          }

          const allThreadsRequest = this.chatService.searchThreads({
            value: searchValue,
            pageSize: 100,
            sortBy: 'last_sent_at',
            sortDirection: 'desc',
          });
          const myThreadsRequest = this.chatService.searchThreads({
            value: searchValue,
            assigneeId: this.ownUserId(),
            pageSize: 100,
            sortBy: 'last_sent_at',
            sortDirection: 'desc',
          });

          return this.combineThreadRequests(allThreadsRequest, myThreadsRequest);
        })
      )
      .subscribe(result => {
        this.initialThreadsLoading.set(false);

        if (!this.features().showThreadTabs) {
          const { threads, unreadsMap } = result as { threads: ChatThread[]; unreadsMap: Map<string, number> };

          this.threads.set(this.sortThreads(threads, unreadsMap));
          this.unreads.set(unreadsMap);
        } else {
          const { combinedThreads, unreadsMap } = result as { combinedThreads: ChatThread[]; unreadsMap: Map<string, number> };

          this.loadTabCounts(true);
          if (!this.isSearching()) {
            this.allThreads.set(combinedThreads);
            this.updateAllTabSignals(combinedThreads);
          }
          this.unreads.set(unreadsMap);
          this.filterThreadsForActiveTab();
        }

        this.threadsLoading.set(false);
      });
  }

  private combineThreadRequests(allThreadsRequest: Observable<any>, myThreadsRequest: Observable<any>) {
    return allThreadsRequest.pipe(
      switchMap((allResult: any) =>
        myThreadsRequest.pipe(
          map((myResult: any) => {
            const allThreads: ChatThread[] = [...(allResult.value || []), ...(myResult.value || [])];
            const uniqueThreads = allThreads.reduce((unique: ChatThread[], thread: ChatThread) => {
              if (!unique.find((t: ChatThread) => t.id === thread.id)) unique.push(thread);
              return unique;
            }, [] as ChatThread[]);

            return uniqueThreads.sort((a: ChatThread, b: ChatThread) => new Date(b.lastSentAt ?? 0).getTime() - new Date(a.lastSentAt ?? 0).getTime());
          }),
          switchMap((combinedThreads: ChatThread[]) =>
            this.chatService.getUnreadCountsByThreadIds(combinedThreads).pipe(
              map(unreadsResult => {
                const unreadsMap = new Map<string, number>();
                Object.entries(unreadsResult).forEach(([threadId, count]) => {
                  unreadsMap.set(threadId, +count);
                });

                return { combinedThreads, unreadsMap };
              })
            )
          )
        )
      )
    );
  }

  private initSelectedThreadMessagesPolling() {
    interval(POLLING_INTERVAL_MESSAGES)
      .pipe(
        filter(() => !!this.selectedThread()),
        switchMap(() => {
          const from = this.messages().at(-1) ? new Date(this.messages().at(-1).createdAt.getTime() + 1) : undefined;

          return this.chatService.searchChatMessages(this.selectedThread().id, {
            from,
            pageSize: MESSAGE_LIMIT,
          });
        })
      )
      .subscribe(result => {
        if (result.value?.length > 0) this.upsertMessages(result.value);
      });
  }

  private initNotificationHandlers() {
    this.notificationService.registerHandler({
      type: ChatNotificationType.MessageUnread,
      showSystemNotification: false,
      updateState: true,
      handler: (payload: ChatNotificationPayload) => {
        if (this.selectedThread()?.id === payload.threadId) {
          const d = new Date(payload.date);

          d.setSeconds(d.getSeconds() - 1);
          this.onMessagesAfter(d);
        }
        this.chatService
          .getThread(payload.threadId)
          .pipe(
            // TODO: with this comment Firebase notification won't trigger threads update
            //       this is temporary decision and need to be fixed in scope of push-notifications refactoring OR whole chat refactoring
            // tap(thread => {
            //   if (!this.isSearching()) this.upsertThreads([thread]);
            // }),
            switchMap(() => this.chatService.getUnreadCount(payload.threadId))
          )
          .subscribe(result => {
            const unreads = this.unreads();

            unreads.set(payload.threadId, result.count);
            this.unreads.set(new Map(unreads));
          });
      },
    });

    this.notificationService.registerHandler({
      type: ChatNotificationType.MessageReceived,
      showSystemNotification: true,
      updateState: false,
      handler: (payload: ChatNotificationPayload) => {
        if (payload.threadId) this.loadThreadData(payload.threadId);
      },
    });
  }

  private loadMessages(thread: ChatThread) {
    this.messagesLoading.set(true);
    this.messages.set([]);

    this.chatService
      .searchChatMessages(thread.id, {
        pageSize: MESSAGE_LIMIT,
      })
      .pipe(
        retry({ count: 2, delay: 1000 }),
        catchError(error => {
          console.error('Failed to load messages after retries:', error);
          this.messagesLoading.set(false);

          return EMPTY;
        })
      )
      .subscribe(res => {
        this.upsertMessages(res.value);
        this.messagesLoading.set(false);
      });
  }

  onMessagesBefore(timestamp: Date) {
    this.messagesLoading.set(true);
    this.scroll$.next({ before: timestamp, after: null });
  }

  onMessagesAfter(timestamp: Date) {
    this.messagesLoading.set(true);
    this.scroll$.next({ before: null, after: timestamp });
  }

  onMessagesRead() {
    const unreadsMap = this.unreads();

    unreadsMap.set(this.selectedThread().id, 0);
    this.unreads.set(new Map(unreadsMap));
    this.chatService.markThreadRead(this.selectedThread().id).subscribe();
  }

  onThreadSearch($event: string) {
    const isSearching = !!$event;

    this.searchTerm.set($event === '' ? undefined : $event);
    this.isSearching.set(isSearching);

    if (isSearching) {
      this.threadsLoading.set(true);

      const query = {
        value: $event,
        sortBy: 'last_sent_at',
        sortDirection: 'desc',
        pageSize: THREAD_LIMIT,
      };

      this.chatService.searchThreads(query).subscribe(result => {
        this.threads.set(result.value);
        this.threadsLoading.set(false);
      });
    } else this.filterThreadsForActiveTab();
  }

  assignThread(threadId: string, assigneeId?: string) {
    const apiCall = assigneeId ? this.chatService.assignThread(threadId, assigneeId) : this.chatService.assignThread(threadId);

    return apiCall.pipe(
      concatMap(() => this.chatService.getThread(threadId)),
      tap(thread => {
        const isAssignedToMe = thread.assignees?.includes(this.ownUserId());

        if (this.features().showThreadTabs) {
          this.updateThreadInAllLists(thread);

          if (isAssignedToMe && this.currentActiveTab() !== ChatTabFilter.MY_CHATS) this.navigateToTab(ChatTabFilter.MY_CHATS);
          else if (!isAssignedToMe && this.currentActiveTab() === ChatTabFilter.MY_CHATS) this.navigateToTab(ChatTabFilter.ALL_CHATS);
        } else this.updateThreadInSimpleList(thread);

        if (this.selectedThread() && this.selectedThread().id === thread.id) this.selectedThread.set(thread);
      }),
      catchError(error => {
        throw error;
      })
    );
  }

  unassignThread(threadId: string) {
    return this.chatService.unassignThread(threadId).pipe(
      concatMap(() => this.chatService.getThread(threadId)),
      tap(thread => {
        if (this.features().showThreadTabs) {
          const isUnassigned = !thread.assignees || thread.assignees.length === 0;

          this.updateThreadInAllLists(thread);
          if (isUnassigned && this.currentActiveTab() !== ChatTabFilter.IN_QUEUE) this.navigateToTab(ChatTabFilter.IN_QUEUE);
        } else this.updateThreadInSimpleList(thread);

        if (this.selectedThread() && this.selectedThread().id === thread.id) this.clearSelectedThread();
      }),
      catchError(error => {
        throw error;
      })
    );
  }
  private getCurrentSenderName(): string {
    const dynamicUser = this.chatConfig.getUserInfo?.();

    return dynamicUser?.senderName || this.chatConfig.ownUserInfo?.senderName || 'User';
  }

  private replacePendingMessage(tempId: string, realMessage: ChatMessage): void {
    const current = this.messages();
    const updated = current.map(msg => (msg.id === tempId ? realMessage : msg));

    this.messages.set(updated);
  }

  private markMessageAsFailed(tempId: string): void {
    const current = this.messages();
    const updated = current.map(msg => (msg.id === tempId ? { ...msg, isPending: false, hasFailed: true } : msg));

    this.messages.set(updated);
  }

  retryFailedMessage(tempId: string): void {
    const current = this.messages();
    const failedMessage = current.find(msg => msg.id === tempId && msg.hasFailed);
    const updated = current.map(msg => (msg.id === tempId ? { ...msg, isPending: true, hasFailed: false } : msg));

    if (!failedMessage) return;
    this.messages.set(updated);

    const messagePayload: Partial<ChatMessage> = {
      content: failedMessage.content,
      threadId: failedMessage.threadId,
      topicId: failedMessage.topicId,
      messageType: failedMessage.messageType,
      externalId: failedMessage.externalId,
      media: failedMessage.media,
      senderName: this.getCurrentSenderName(),
    };

    this.chatService
      .sendMessage(messagePayload)
      .pipe(
        tap(result => this.replacePendingMessage(tempId, result)),
        switchMap(result => this.chatService.getThread(result.threadId))
      )
      .subscribe({
        next: thread => {
          this.upsertThreads([thread]);
          this.markThreadAsRead(thread.id);
        },
        error: () => this.markMessageAsFailed(tempId),
      });
  }

  onTabFilterChange(searchRequest: ChatThreadSearchRequest) {
    this.currentSearchRequest.set(searchRequest);

    if (searchRequest.assigneeId === undefined) this.currentActiveTab.set(ChatTabFilter.ALL_CHATS);
    else if (searchRequest.assigneeId !== null) this.currentActiveTab.set(ChatTabFilter.MY_CHATS);
    else if (searchRequest.assigneeId === null) this.currentActiveTab.set(ChatTabFilter.IN_QUEUE);

    this.filterThreadsForActiveTab();
    this.threadsLoading.set(false);
  }

  private filterThreadsForActiveTab() {
    const allThreads = this.allThreads();
    const currentUserId = this.ownUserId();
    const unreadsMap = this.unreads();
    const activeTab = this.currentActiveTab();
    let filteredThreads: ChatThread[];
    let sortedThreads: ChatThread[];

    if (allThreads.length === 0 || !currentUserId) return;

    switch (activeTab) {
      case ChatTabFilter.ALL_CHATS:
        sortedThreads = this.sortThreads(allThreads, unreadsMap);
        this.allThreads.set(sortedThreads);
        this.threads.set(sortedThreads);
        break;

      case ChatTabFilter.MY_CHATS:
        filteredThreads = allThreads.filter(thread => thread.assignees?.includes(currentUserId));
        sortedThreads = this.sortThreads(filteredThreads, unreadsMap);
        this.myThreads.set(sortedThreads);
        this.myChatsCount.set(sortedThreads.length);
        this.threads.set(sortedThreads);
        break;

      case ChatTabFilter.IN_QUEUE:
        filteredThreads = allThreads.filter(thread => (!thread.assignees || thread.assignees.length === 0) && (unreadsMap.get(thread.id) || 0) > 0);
        sortedThreads = this.sortThreads(filteredThreads, unreadsMap);
        this.inQueueThreads.set(sortedThreads);
        this.inQueueCount.set(sortedThreads.length);
        this.threads.set(sortedThreads);
        break;

      default:
        sortedThreads = this.sortThreads(allThreads, unreadsMap);
        this.threads.set(sortedThreads);
    }
  }

  private updateAllTabSignals(allThreads: ChatThread[]) {
    if (!this.ownUserId()) return;

    const unreadsMap = this.unreads();
    const myThreads = allThreads.filter(thread => thread.assignees?.includes(this.ownUserId()));
    const sortedMyThreads = this.sortThreads(myThreads, unreadsMap);
    const inQueueThreads = allThreads.filter(thread => (!thread.assignees || thread.assignees.length === 0) && (unreadsMap.get(thread.id) || 0) > 0);
    const sortedInQueueThreads = this.sortThreads(inQueueThreads, unreadsMap);

    this.myThreads.set(sortedMyThreads);
    this.myChatsCount.set(sortedMyThreads.length);
    this.inQueueThreads.set(sortedInQueueThreads);
    this.inQueueCount.set(sortedInQueueThreads.length);
    this.filterThreadsForActiveTab();
  }

  loadTabCounts(force = false) {
    if (!force && Date.now() - this.lastCountUpdate < this.THROTTLE_DELAY) return;
    if (!this.ownUserId()) return;

    this.lastCountUpdate = Date.now();

    this.chatService.getThreadsCount(this.ownUserId()).subscribe(count => this.myChatsCount.set(count));
    this.updateCountsFromCurrentState();
  }

  private updateCountsFromCurrentState() {
    if (!this.ownUserId()) return;
    if (this.allThreads().length === 0) return;

    const myChatsFromAll = this.allThreads().filter(thread => thread.assignees?.includes(this.ownUserId()));
    const inQueueFromAll = this.allThreads().filter(thread => (!thread.assignees || thread.assignees.length === 0) && (this.unreads().get(thread.id) || 0) > 0);

    this.myChatsCount.set(myChatsFromAll.length);
    this.inQueueCount.set(inQueueFromAll.length);
  }

  private loadSystemUsers() {
    if (!this.systemUsersService) return;

    this.systemUsersService.getAll().subscribe({
      next: users => this.systemUsers.set(users),
      error: error => console.error('Failed to load assignees:', error),
    });
  }

  private navigateToTab(filter: ChatTabFilter) {
    let chatTabParam: string;

    switch (filter) {
      case ChatTabFilter.IN_QUEUE:
        chatTabParam = 'awaiting';
        break;
      case ChatTabFilter.MY_CHATS:
        chatTabParam = 'myChats';
        break;
      default:
        chatTabParam = 'all';
        break;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { chatTab: chatTabParam },
      queryParamsHandling: 'merge',
    });
  }

  private addThreadToMyChats(thread: ChatThread) {
    const currentMyThreads = this.myThreads();
    const existingIndex = currentMyThreads.findIndex(t => t.id === thread.id);
    let updatedMyThreads: ChatThread[];

    if (existingIndex >= 0) updatedMyThreads = currentMyThreads.map(t => (t.id === thread.id ? thread : t));
    else updatedMyThreads = [thread, ...currentMyThreads];

    this.myThreads.set(updatedMyThreads);
    this.myChatsCount.set(updatedMyThreads.length);
    if (this.currentActiveTab() === ChatTabFilter.MY_CHATS) this.threads.set(updatedMyThreads);
  }

  private removeThreadFromMyChats(threadId: string) {
    const currentMyThreads = this.myThreads();
    const updatedMyThreads = currentMyThreads.filter(t => t.id !== threadId);

    if (updatedMyThreads.length !== currentMyThreads.length) {
      this.myThreads.set(updatedMyThreads);
      this.myChatsCount.set(updatedMyThreads.length);
      if (this.currentActiveTab() === ChatTabFilter.MY_CHATS) this.threads.set(updatedMyThreads);
    }
  }

  private removeThreadFromInQueue(threadId: string) {
    const currentInQueueThreads = this.inQueueThreads();
    const updatedInQueueThreads = currentInQueueThreads.filter(t => t.id !== threadId);

    if (updatedInQueueThreads.length !== currentInQueueThreads.length) {
      this.inQueueThreads.set(updatedInQueueThreads);
      this.inQueueCount.set(updatedInQueueThreads.length);
      if (this.currentActiveTab() === ChatTabFilter.IN_QUEUE) this.threads.set(updatedInQueueThreads);
    }
  }

  private addThreadToInQueue(thread: ChatThread) {
    const currentInQueueThreads = this.inQueueThreads();
    const existingIndex = currentInQueueThreads.findIndex(t => t.id === thread.id);
    const unreadsMap = this.unreads();
    const hasUnread = (unreadsMap.get(thread.id) || 0) > 0;
    let updatedInQueueThreads: ChatThread[];

    if (!hasUnread) return;
    if (existingIndex >= 0) updatedInQueueThreads = currentInQueueThreads.map(t => (t.id === thread.id ? thread : t));
    else updatedInQueueThreads = [thread, ...currentInQueueThreads];

    this.inQueueThreads.set(updatedInQueueThreads);
    this.inQueueCount.set(updatedInQueueThreads.length);
    if (this.currentActiveTab() === ChatTabFilter.IN_QUEUE) this.threads.set(updatedInQueueThreads);
  }

  private updateThreadInAllLists(thread: ChatThread) {
    if (!this.ownUserId()) return;

    const isAssignedToMe = thread.assignees?.includes(this.ownUserId());
    const isUnassigned = !thread.assignees || thread.assignees.length === 0;
    const currentAllThreads = this.allThreads();
    const updatedAllThreads = currentAllThreads.map(t => (t.id === thread.id ? thread : t));

    if (!currentAllThreads.find(t => t.id === thread.id)) updatedAllThreads.unshift(thread);
    this.allThreads.set(updatedAllThreads);

    if (isAssignedToMe) this.addThreadToMyChats(thread);
    else this.removeThreadFromMyChats(thread.id);

    if (isUnassigned && !isAssignedToMe) this.addThreadToInQueue(thread);
    else this.removeThreadFromInQueue(thread.id);

    if (this.currentActiveTab() === ChatTabFilter.ALL_CHATS) this.threads.set(updatedAllThreads);
  }

  private updateThreadInSimpleList(thread: ChatThread) {
    const existingThreadIndex = this.threads().findIndex(t => t.id === thread.id);

    if (existingThreadIndex >= 0) this.threads.set(this.threads().map(t => (t.id === thread.id ? thread : t)));
    else this.threads.set([thread, ...this.threads()]);
  }

  private sortThreads(threads: ChatThread[], unreadsMap: Map<string, number>): ChatThread[] {
    const unreadThreads: ChatThread[] = [];
    const readThreads: ChatThread[] = [];

    threads.forEach(thread => {
      if ((unreadsMap.get(thread.id) || 0) > 0) unreadThreads.push(thread);
      else readThreads.push(thread);
    });

    unreadThreads.sort((a, b) => {
      if (!a.lastSentAt) return 1;
      if (!b.lastSentAt) return -1;

      return new Date(a.lastSentAt).getTime() - new Date(b.lastSentAt).getTime();
    });

    readThreads.sort((a, b) => {
      if (!a.lastSentAt) return 1;
      if (!b.lastSentAt) return -1;

      return new Date(b.lastSentAt).getTime() - new Date(a.lastSentAt).getTime();
    });

    return [...unreadThreads, ...readThreads];
  }
}
